import React, { useState, useEffect } from 'react';
import { FiServer, FiDatabase, FiCpu, FiHardDrive, FiActivity, FiCheckCircle, FiXCircle, FiAlertCircle } from 'react-icons/fi';
import { getSystemHealth } from '../services/system';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import toast from 'react-hot-toast';

export default function SystemHealth() {
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchHealth = async () => {
    try {
      const data = await getSystemHealth();
      setHealth(data.data);
    } catch (error) {
      toast.error('Failed to fetch system health');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${days}d ${hours}h ${minutes}m ${secs}s`;
  };

  if (loading) return <LoadingSpinner />;
  if (!health) return <div className="p-6 text-center">No data available</div>;

  const dbStatus = health.database?.status === 'connected';

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">System Health</h1>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">API Status</p>
              <div className="flex items-center mt-1">
                <FiCheckCircle className="text-green-500 mr-2" />
                <span className="text-lg font-semibold text-green-600">Online</span>
              </div>
            </div>
            <FiServer className="w-10 h-10 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Database Status</p>
              <div className="flex items-center mt-1">
                {dbStatus ? (
                  <>
                    <FiCheckCircle className="text-green-500 mr-2" />
                    <span className="text-lg font-semibold text-green-600">Connected</span>
                  </>
                ) : (
                  <>
                    <FiXCircle className="text-red-500 mr-2" />
                    <span className="text-lg font-semibold text-red-600">Disconnected</span>
                  </>
                )}
              </div>
            </div>
            <FiDatabase className={`w-10 h-10 ${dbStatus ? 'text-green-500' : 'text-red-500'}`} />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Uptime</p>
              <p className="text-lg font-semibold text-gray-800 mt-1">{formatUptime(health.server?.uptime || 0)}</p>
            </div>
            <FiActivity className="w-10 h-10 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Database Details */}
      {health.database && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <FiDatabase className="mr-2 text-primary-500" /> Database Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Database Name</p>
              <p className="font-medium">{health.database.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Collections</p>
              <p className="font-medium">{health.database.collections}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Indexes</p>
              <p className="font-medium">{health.database.indexes}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Data Size</p>
              <p className="font-medium">{formatBytes(health.database.dataSize)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Storage Size</p>
              <p className="font-medium">{formatBytes(health.database.storageSize)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Server Details */}
      {health.server && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <FiCpu className="mr-2 text-primary-500" /> Server Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Node Version</p>
              <p className="font-medium">{health.server.node_version}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Platform</p>
              <p className="font-medium">{health.server.platform} ({health.server.arch})</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">CPUs</p>
              <p className="font-medium">{health.server.cpus} cores</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Load Average</p>
              <p className="font-medium">{health.server.load_average?.map(l => l.toFixed(2)).join(', ')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Memory</p>
              <p className="font-medium">{formatBytes(health.server.total_memory)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Free Memory</p>
              <p className="font-medium">{formatBytes(health.server.free_memory)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Heap Used</p>
              <p className="font-medium">{formatBytes(health.server.memory?.heapUsed)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Heap Total</p>
              <p className="font-medium">{formatBytes(health.server.memory?.heapTotal)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
